import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useLinkedinGetProfileView } from 'crono-fe-common/hooks/crono-extension/gateway';
import {
  isFindNewContactProfileComplete,
  mapFindNewContactToProfileExtendedInfo,
} from 'crono-fe-common/utils/findNew/mappers';
import { FindNewSourceAnalyticsEvent } from 'crono-fe-common/types/analyticsFindNewSource';
import { useAnalytics } from 'context/analytics';
import useFindNewContactLookup from './useFindNewContactLookup';

type ProfileViewResult = ReturnType<typeof useLinkedinGetProfileView>['result'];

type CallParams = Parameters<
  ReturnType<typeof useLinkedinGetProfileView>['call']
>[0];

export default function useProfileViewWithFallback() {
  const {
    call: linkedinCall,
    resetData: linkedinResetData,
    error: linkedinError,
  } = useLinkedinGetProfileView();

  const { call: findNewContactLookup } = useFindNewContactLookup();
  const analytics = useAnalytics();

  const {
    mutateAsync: call,
    data: result,
    isLoading,
    reset: resetMutation,
  } = useMutation<ProfileViewResult, unknown, CallParams>({
    // Clear stale LinkedIn state so a fresh DB hit can't surface a prior call's
    // error (the DB phase never sets one).
    onMutate: linkedinResetData,
    mutationFn: async (params) => {
      if (params.publicId) {
        const dto = await findNewContactLookup({ publicId: params.publicId });
        if (dto != null && isFindNewContactProfileComplete(dto)) {
          analytics?.track(FindNewSourceAnalyticsEvent.ProfileViewInternal);
          return { profile: mapFindNewContactToProfileExtendedInfo(dto) };
        }
      }

      const linkedinResult = await linkedinCall(params);
      if (linkedinResult?.profile != null) {
        analytics?.track(FindNewSourceAnalyticsEvent.ProfileViewLinkedin);
      }
      return linkedinResult;
    },
  });

  const resetData = useCallback(() => {
    resetMutation();
    linkedinResetData();
  }, [resetMutation, linkedinResetData]);

  return {
    result: result ?? null,
    error: linkedinError,
    isLoading,
    call,
    resetData,
  };
}
